import { useMutation, useQuery } from '@apollo/client'
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js'
import {
  PaymentRequest,
  PaymentRequestShippingAddress,
  PaymentRequestShippingAddressEvent,
  PaymentRequestShippingOption,
  PaymentRequestShippingOptionEvent,
  PaymentRequestTokenEvent,
} from '@stripe/stripe-js'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import { Dispatch, useEffect, useMemo, useRef } from 'react'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { IAction, IApplePayEnabled, IErrorAction } from 'checkout/containers/CheckoutStateReducer'
import { PartialOrder } from 'checkout/types/IOrder'
import {
  getStripeBuyData,
  getStripeDisplayOptions,
  getStripePaymentRequestOptions,
} from 'checkout/utils/stripePaymentOptions'
import { useAuth } from 'shared/hooks/useAuth'
import useCurrency from 'shared/hooks/useCurrency'
import { isTimeoutError, timeoutMessage } from '../utils/timeout'

interface IUseApplePayProps {
  dispatch: Dispatch<IAction | IErrorAction | IApplePayEnabled>
  displayCheckoutRTK: boolean
  order?: PartialOrder
}

const PURCHASE_WITH_APPLEPAY = gql`
  mutation buyApplePay($input: ExpressPayOrderInput!) {
    buyApplePay(input: $input)
  }
`

const GET_PRICE_ESTIMATE = gql`
  query getPartialOrder($input: PartialOrderInput!) {
    getPartialOrder(input: $input) {
      finalPrice {
        value
        localizedValue
      }
      taxPrice {
        value
        localizedValue
      }
      shippingPrice {
        value
        localizedValue
      }
      credits {
        value
        localizedValue
      }
      productPrice {
        value
        localizedValue
      }
      shippingServiceLevelOptions {
        id
        name
        description
        price {
          value
          localizedValue
          display
        }
        available
      }
      shippingServiceLevel {
        id
        name
        description
        price {
          value
          localizedValue
          display
        }
        available
      }
      promotion {
        id
        title
        description
        expiration
        value {
          display
          value
          localizedValue
        }
      }
    }
  }
`

export interface IAddressInput {
  city: string
  countryCode: string
  name: string
  address1: string
  address2?: string
  postalCode?: string
  phone?: string
  state: string
}

interface IBillingInfoInput {
  name?: string
  orderNumber?: number
  stripeToken: string
}

interface IProductInput {
  productTemplate?: {
    id?: number
  }
  size?: {
    value?: number
  }
  id?: number
}
export interface IExpressPayOrderInput {
  billingInfo: IBillingInfoInput
  billingAddress: IAddressInput
  email?: string
  finalPriceCents: number
  isInstantShip?: boolean
  orderNumber?: number
  product?: IProductInput
  shippingAddress: IAddressInput
  shippingServiceLevelId: string
}

type PaymentRequestUpdateEvents =
  | PaymentRequestShippingAddressEvent
  | PaymentRequestShippingOptionEvent

const useApplePay = ({ dispatch, displayCheckoutRTK, order }: IUseApplePayProps) => {
  const stripe = useStripe()
  const router = useRouter()
  const { logoutUser } = useAuth()
  const { selectedCurrency } = useCurrency()
  const [buyWithApplePay] = useMutation<{ buyApplePay: number }, { input: IExpressPayOrderInput }>(
    PURCHASE_WITH_APPLEPAY,
  )
  const { refetch: getPriceEstimate } = useQuery(GET_PRICE_ESTIMATE, { skip: true })

  const paymentRequest = useRef<PaymentRequest>()
  const { locale } = useRouter()

  // ApplePayButton will not re-render while the sheet is popped
  // so we need a ref for shippingAddress / shippingOption
  const shippingAddress = useRef<PaymentRequestShippingAddress>()
  const shippingOption = useRef<PaymentRequestShippingOption>()
  const updatedOrder = useRef<PartialOrder>()

  // We do not send apple any address info, all address info comes from what the user has saved for
  // apple pay, initially our order pricing is based off our stored address. Upon opening of apple
  // pay window, shippingaddresschange event is sent and we can update order based on apple
  // address info (which is only a partial address so we update on an order esitmate, but do not
  // update the actual order until checkout )
  const handleShippingUpdate = async (event: PaymentRequestUpdateEvents) => {
    if ('shippingAddress' in event) {
      shippingAddress.current = event.shippingAddress
    }
    if ('shippingOption' in event) {
      shippingOption.current = event.shippingOption
    }
    try {
      const { data } = await getPriceEstimate({
        input: {
          city: shippingAddress.current?.city,
          countryCode: shippingAddress.current?.country,
          postalCode: shippingAddress.current?.postalCode,
          state: shippingAddress.current?.region,
          orderNumber: updatedOrder.current?.orderNumber,
          shippingServiceLevel:
            shippingOption.current?.id || updatedOrder.current?.shippingServiceLevel?.id,
          productTemplateId: Number(updatedOrder.current?.product?.productTemplate?.id) || null,
          size: Number(updatedOrder.current?.product?.size?.value) || null,
          instantShip: updatedOrder.current?.isInstantShip === true,
          productId: Number(updatedOrder.current?.product?.id) || null,
        },
      })
      updatedOrder.current = Object.assign({}, updatedOrder.current, data.getPartialOrder)
      const displayOptions = getStripeDisplayOptions(updatedOrder.current)
      displayOptions.status = 'success'
      event.updateWith(displayOptions)
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'expressCheckoutFailure',
        payload: error.message,
      })
      event.updateWith({ status: 'fail' })
    }
  }

  // paymentTokens are generated after a user authorizes an applePay transation.
  // Sneakers uses the token to complete payment via stripe
  const handlePaymentToken = async (event: PaymentRequestTokenEvent) => {
    try {
      dispatch({ type: 'expressCheckoutRequest' })
      const expressPayOrder = getStripeBuyData({ order: updatedOrder.current, event })
      const { data } = await buyWithApplePay({ variables: { input: expressPayOrder } })
      const orderNumber = data?.buyApplePay
      event.complete('success')
      router.push(`/checkout/${orderNumber}/success`, `/checkout/${orderNumber}/success`, {
        locale,
      })
    } catch (error) {
      Bugsnag.notify(error as NotifiableError)
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
      } else {
        dispatch({
          type: 'expressCheckoutFailure',
          payload: error.message,
        })
      }
      event.complete('fail')
    }
  }

  const createStripeSessionAndCheckCapability = async () => {
    const paymentRequestOptions = getStripePaymentRequestOptions({
      order,
      selectedCurrencyCode: selectedCurrency?.isoCode,
    })
    paymentRequest.current = stripe?.paymentRequest(paymentRequestOptions)
    paymentRequest.current?.on('shippingaddresschange', handleShippingUpdate)
    paymentRequest.current?.on('shippingoptionchange', handleShippingUpdate)
    paymentRequest.current?.on('token', handlePaymentToken)
    const canMakePayment = await paymentRequest.current?.canMakePayment()
    dispatch({
      type: 'applePayAvailable',
      payload: !!canMakePayment,
    })
  }

  useEffect(() => {
    if (displayCheckoutRTK) {
      return
    }
    if (stripe && !!order) {
      updatedOrder.current = order
      createStripeSessionAndCheckCapability()
    }
  }, [displayCheckoutRTK, stripe, order])

  // Update the displayItems object if promotion id changes to capture
  // new promotion price
  useEffect(() => {
    if (stripe) {
      updatedOrder.current = order
      paymentRequest.current?.update(getStripeDisplayOptions(order))
    }
  }, [stripe, order?.promotion?.id, !!paymentRequest.current])

  // useMemo stops the paymentRequestButton
  // from re-rendering / flickering
  const ApplePayButton = useMemo(() => {
    if (paymentRequest.current) {
      return () => (
        <PaymentRequestButtonElement options={{ paymentRequest: paymentRequest.current! }} />
      )
    }
    return () => null
  }, [!!paymentRequest.current])

  return { ApplePayButton }
}

export default useApplePay
